import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {IdynamicFormItem, ItoPathFromPath} from './interfaces/IdynamicFormItem';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Subject} from 'rxjs/Subject';
import {Observable} from 'rxjs/Observable';
import {FormHandlerService} from './services/form-handler.service';
import {IgenomicsState} from '../../core/interfaces/genomics-state.interface';
import {select, Store} from '@ngrx/store';
import {ITEMS_TYPES} from './constants/ItemsTypes';
import {findIndex as _findIndex, cloneDeep, get, includes, isEmpty, isEqual, mapKeys as _mapKeys, set, toPath, uniqBy as _uniqBy,remove as _remove, has} from 'lodash';
import {InterpolateService} from '../services/interpolate.service';
import {IReflect} from './interfaces/reflect.interface';
import {DynamicFormItem} from './classes/dynamic-form-item.class';
import {
  debounce,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  skipWhile,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import {ResourceReference} from '../../core/classes/resource-reference.class';
import {RoutingChangeHandlerService} from '../../layout/services/routing-change-handler.service';
import {StringUtils} from '@ispm/shared/utils/String.utils';
import {DynamicFormModalPayload} from '@ispm/dynamic-form/classes/dynamic-form-modal-payload.class';
import {IEService} from '@ispm/shared/services/IE.service';
import {IWidgetItem} from '@ispm/widgets/interfaces/widget-item.interface';
import {BehaviorSubject} from 'rxjs';
import {AsyncLoaderClass} from '@ispm-client/modules/aggregator/class/asyncloader';
import {AgregationService, FlattenService} from '@ispm-client/modules/aggregator/services';
import {
  applyValueTransformation,
  checkConditions,
  checkMax,
  checkMin,
  fillInterpolateString,
  getDisplayedValue,
  getFormItemValue,
  getRequiredToSet,
  getValueToSet,
  getValueToSetFromBulkValue, getValueToSetFromReflectedItem,
  setConfiguration,
  setCssClass,
  setDisplayPattern,
  setLabel,
  setMinAndMaxAttributes,
  setOptions,
  setPlaceholder,
  setRemovePropertyFlag,
  setUpdateFormInstanceOnReflect,
  validateRegex
} from '@ispm/dynamic-form/utilities/reflects.util';
import {VALUE_TYPES} from '@ispm/dynamic-form/constants/value-type.enum';
import {getValueTypeByItem} from '@ispm/dynamic-form/utilities/item.util';
import {WidgetValueWrapper} from '@ispm/dynamic-form/classes/widget-value-wrapper.class';
import {selectInputConfig} from '@ispm/dynamic-form/store/form.selectors';
import {DEFAULT_LOADER_FOR_DYNAMIC_FORM, LOADER_TYPES} from '@ispm/core/constants/core-ui-constants';
import { CARD_ACTIONS } from "@ispm-client/modules/pan-cancer/constants/constants";
import * as jmespath from 'jmespath';
import {AsyncWidget} from '@ispm-client/modules/widgets/decorator/async-widget.decorator';
import {NOTIFICATION_MESSAGE} from "@ispm-client/modules/aggregator/services/consts";
import moment from 'moment';
import {IdataBinding} from "@ispm-client/modules/aggregator/interfaces";
import {SearchService} from "@ispm/dynamic-form/services/search.service";
import {IWorklistColumnItem} from "../../worklist/interfaces/worklist-action.interface";
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';

@Component({
  selector: 'gen-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})

@AsyncWidget('DynamicForm')
export class DynamicFormComponent extends AsyncLoaderClass implements OnInit, OnDestroy, OnChanges, IWidgetItem {
  public updateDataBinding: boolean = false;
  public isValid: boolean;
  public normalSpecimenStatus: boolean;

  @Input() disabledOptions: string[] = [];

  @Input()
  set formReflects(formReflects: IReflect[]) {
    this._formReflects = formReflects;

    if (!isEmpty(this._formReflects) && this.formId && this.dynamicForm) {
      this.runFormReflects(true);
    }
  }

  get formReflects() {
    return this._formReflects;
  }

  @Input()
  set triggerReflects(fields: string[]) {
    if (!this.dynamicForm) {
      this.formInit
        .pipe(take(1))
        .subscribe(() => {
          this.triggerReflectsForField(fields);
        });
    } else {
      this.triggerReflectsForField(fields);
    }
  }

  @Input()
  set reInitializeForm(reinitialize: boolean) {
    this._resetStatus = reinitialize;
    if (reinitialize) {
      this.reset();
      this.initForm();
      this._ngZone.runOutsideAngular(() => {
        setTimeout(() => this.resetForm = false, 0);
      });
    }
  }
  _defaultList:Array<any>;
  @Input()
  set defaultList(list: Array<any>) {
    this._defaultList = list;
  }
  @Input()
  set resetForm(reset: boolean) {
    this._resetStatus = reset;

    if (reset) {
      this.reset();
      this.formData = null;
      this.initForm();
      this._ngZone.runOutsideAngular(() => {
        setTimeout(() => this.resetForm = false, 0);
      });
    } else {
      this.resetStatusEmitter.emit(this._resetStatus);
    }
  }

  get resetForm() {
    return this._resetStatus;
  }

  @Input()
  set cancelFetchUpdatedFields(cancelFetch: boolean) {
    this._cancelFetchUpdatedFields = cancelFetch;
  }

  get cancelFetchUpdatedFields(): boolean {
    return this._cancelFetchUpdatedFields;
  }
  @Input()
  set updatedFieldsContext(context: string) {
    this._updatedFieldsContext = context;

    if (this.updatedFieldsContext && this.dynamicForm) {
      this.getChangedFields();
    }
  }

  get updatedFieldsContext(): string {
    return this._updatedFieldsContext;
  }

  @Input()
  set diffFields(fields: Array<string>) {
    this._diffFields = new Set<string>(fields);
    this.markDifferences();

    if (this.submitted) {
      this.updateChangedFields().subscribe();
    }
  }

  get diffFields() {
    return Array.from(this._diffFields || []);
  }

  /******* GEN-16525 diff mitigation - end ******/

  @Input()
  get valid() {
    return this.validateValue;
  }

  set valid(newVal) {
    if (this.emitFormChangeEvenIfInvalid) {
      this.validateChange.emit(newVal);
    }

    if (this.validateValue !== newVal || this.forceNotifyValidation) {
      this.validateValue = newVal;
      this.validateChange.emit(this.validateValue);
      this.forceNotifyValidation = false;
    }
  }

  @Input()
  set overrides(overrides: { [fieldName: string]: Partial<IdynamicFormItem> }) {
    if (!overrides) {
      return;
    } else if (JSON.stringify(overrides) === JSON.stringify(this._overrides)) {
      return;
    }

    this._overrides = overrides;
    this.initForm();
  }

  get overrides() {
    return this._overrides;
  }

  @Input()
  set disabled(status: boolean) {
    this._disabled = status;

    if (this.dynamicForm) {
      this.disabled ? this.dynamicForm.disable() : this.dynamicForm.enable();
    }
  }

  get disabled() {
    return this._disabled;
  }

  @Input()
  set disabledForced(status: boolean) {
    this._disableForce = status;

    if (this.dynamicForm) {
      this.reset();
      this.initForm();
    }
  }

  get disabledForced() {
    return this._disableForce;
  }

  @Input()
  set editMode(mode: boolean) {
    this._editMode = mode;

    if (this._editMode && this.updateOnBlur === null) {
      this.updateOnBlur = true;
    }

  }

  get editMode() {
    return this._editMode;
  }

  @Input('formName')
  get formId() {
    return this.formIdentifier;
  }

  set formId(value: string) {
    this.cleanLocalForm();

    if ((!value && this.formConfig) || (!!value && this.formIdentifier !== value)) {
      this.formIdentifier = value;
      this.load = false;

      if (this.dynamicForm) {
        if (!this._localForm) {
          this.formConfig = null;
        }

        super.init(this.formId);
        this.reset();
        this.initForm();
      }
    }
  }

  @Input() set itemCount(_showFieldCount: number) {
    this.showFieldCount = _showFieldCount;
  }

  // This input resets the ID of the form in case the configuration is different than the previous one.
  @Input()
  set standaloneFormConfig(formConfig: IdynamicFormItem[]) {
    // Checking if this is the same config
    const isSameConfig = isEqual(formConfig, this.formConfig);

    // If the configuration was different then the whole form should be reinitialized  when setting a new ID
    if (!isSameConfig) {
      this.formId = null;
    }

    this.formConfig = formConfig;
  }

  @Input()
  set formConfig(formConfig: IdynamicFormItem[]) {
    if (formConfig) {
      if(this.noConfigMutation) { // Only first depth cloning. Deep cloning making the nested form wasted rendering
        this._formConfig = [...formConfig];
      } else {
        this._formConfig = cloneDeep(formConfig); // Shallow copy to prevent mutable list, each item treated separately by the getConfig to optimize memory usage
      }
      if (this.isFormConfigAvailable) {
        this.isFormConfigAvailable.next(true);
      }
    }
    else {
      this._formConfig = formConfig;
    }

    if (!this.getFormId) {
      this.generateUUID();
    }
  }
 // converting to getters post angular 10
  get getFormId() {
    return this.formId;
  }
  get formConfig() {
    return this._formConfig;
  }

  @Input()
  set formData(data: any) {
    if (this.flagDiffFieldsOnUpdate) {
      if (this._formConfig) {
        const fields = this.formHandler.getChangedFieldsFromObject(data, this._formData, _mapKeys(this._formConfig, conf => conf.name));
        this.diffFields = [...this.diffFields, ...fields] as string[];
      }
      else {
        const origConfigData: any = this._formData;
        this.isFormConfigAvailable = new BehaviorSubject(false);
        this.isFormConfigAvailable.subscribe((value) => {
          if (value) {
            const fields = this.formHandler.getChangedFieldsFromObject(data, origConfigData, _mapKeys(this._formConfig, conf => conf.name));
            this.diffFields = [...this.diffFields, ...fields] as string[];
            this.isFormConfigAvailable.unsubscribe();
            this.isFormConfigAvailable = null;
        }});
      }
    }

    this._formData = data;
  }

  get formData() {
    return this._formData;
  }

  @Input()
  set formVertical(formVertical: boolean) {
    if (this.isIE) {
      // IE is not triggering change detector since it might set before view init
      setTimeout(() => {
        this._formVertical = formVertical;
      });
    } else {
      this._formVertical = formVertical;
    }
  }

  get formVertical() {
    return this._formVertical;
  }

  @Input()
  set submitted(status: boolean) {
    this._submitted = status;

    if (this.submitted) {
      this.updateChangedFields().subscribe();
    }
  }

  get submitted() {
    return this._submitted;
  }

  /**
   * @deprecated
   * use reflects instead
   * @param updateObject
   */
  @Input()
  set updateField(updateObject: { type: 'update' | 'init', name: string, value: any, property?: string, searchInQuery?: boolean }) {
    // TODO:Remove this logic and replace with "Reflect" configuration - @Orel
    if (!this.dynamicForm) {
      return;
    }

    const updateObjectAsString = JSON.stringify(updateObject);

    if (updateObject
      && updateObject.name
      && (!updateObject.type || updateObject.type === 'update')
      && updateObject.hasOwnProperty('value')) {

      if ((!this.load || this.dependedProperties.has(updateObject.name))
        && this.dynamicForm.controls[updateObject.name]
        && this.lastUpdateObject !== updateObjectAsString) {
        this.lastUpdateObject = updateObjectAsString;

        if (this.lastTouchedItem !== updateObject.name) {
          this.dynamicForm.controls[updateObject.name].patchValue(updateObject.value);
        }

        const dependedObjects = this.dependedProperties.get(updateObject.name) || [];

        if (dependedObjects) {
          dependedObjects.forEach((dependedObject: { target: string; dataSourcePath: string }) => {
            if (dependedObject && updateObject.value) {
              this.dynamicForm.controls[dependedObject.target].patchValue(get(updateObject.value, dependedObject.dataSourcePath));
            }
          });
        }
      }

      const updatedPath = updateObject.property ? `${updateObject.property}.${updateObject.name}` : updateObject.name;

      if (this.dependencyMap.has(updatedPath)) {
        this.dependencyMap.get(updatedPath).forEach(dependedObject => {
          if (this.dynamicForm.controls[dependedObject.target]) {
            this.dynamicForm.controls[dependedObject.target].patchValue(get(updateObject.value, dependedObject.dataSourcePath));
          }
        });
      }
    } else if (updateObject && updateObject.type === 'init') {
      this.lastUpdateObject = null;

      if (updateObject.name && this.lastTouchedItem !== updateObject.name) {
        this.dynamicForm.controls[updateObject.name].reset();
      }

      if (updateObject.searchInQuery) {
        this.formConfig.forEach((config: IdynamicFormItem) => {
          if (config.options
            && config.options.resource
            && InterpolateService.hasInterpolationString(config.options.resource, updateObject.value)
          ) {
            this.dynamicForm.controls[config.name].reset();
          }
        });
      }
    }
  }

  set widgetConfig(widgetConfig: IdynamicFormItem) {
    const widgetPayload = get(widgetConfig, 'widgetPayload', { });
    Object.assign(this, widgetPayload);
    this.initForm();
  }

  set widgetData(widgetData: any) {
    const isDifferent = !this.formData || JSON.stringify(this.formData) !== JSON.stringify(widgetData);
    this.formData = widgetData;

    if (isDifferent || this.load === false) {
      this.initForm();
    }
  }

  set widgetFormInstanceSubmitted(submitted: boolean) {
    this.submitted = submitted;
  }

  constructor(
    protected _aggregationService: AgregationService,
    public form: FormBuilder,
    public formHandler: FormHandlerService,
    private routeChangeHandler: RoutingChangeHandlerService,
    private ieService: IEService,
    store: Store<IgenomicsState>,
    private _ngZone: NgZone,
    private detector: ChangeDetectorRef,
    private _flattenService: FlattenService,
    private _search: SearchService,
    private notificationMessageService: NotificationMessageService,
    private notificationMessage: NOTIFICATION_MESSAGE
  ) {
    super(_aggregationService, store, _flattenService);
    this.forms$ = store.pipe(select(selectInputConfig));
    this.isIE = this.ieService.isIE();
  }

  public static GENERATED_FORM_ID_PREFIX = 'DF_TEMP_';
  private static REFLECT_FIELDS = ['reflectStyleClasses'];

  @Output() dataRecieved: EventEmitter<boolean> = new EventEmitter();
  @Output() cardInfoEmitter: EventEmitter<any> = new EventEmitter();
  @Output() validateChange: EventEmitter<boolean> = new EventEmitter();
  @Output() formChange: EventEmitter<any> = new EventEmitter();
  @Output() formInit: EventEmitter<any> = new EventEmitter();
  @Output() formInitCompleted: EventEmitter<any> = new EventEmitter();
  @Output() cancelEmitter: EventEmitter<any> = new EventEmitter();
  @Output() submitEmitter: EventEmitter<any> = new EventEmitter();
  @Output() unsavedEmitter: EventEmitter<any> = new EventEmitter();
  @Output() linkClick: EventEmitter<string> = new EventEmitter<string>();
  @Output() resetStatusEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() itemTouched: EventEmitter<any> = new EventEmitter();

  @Input() mapAllProperties: boolean = true; // enable mapping fields which are not part of the configuration
  @Input() configBindings: any ;

  public load: boolean = false;
  public blured: boolean;
  public lastTouchedItem: string;
  public updatedFields: Set<string> = new Set();
  public lastUpdateObject: string;
  public formIdentifier: string;
  public isIE: boolean;
  public formBaseWidgetPayload: DynamicFormModalPayload;
  public widgetFormInstance: FormGroup;
  public forceChange: boolean = true;
  public unitHeaderClass: string = 'radiology';

  private _formReflects: IReflect[] = [];
  private _externalDataReset: boolean = false;
  private showFieldCount: number;
  private _nestedObjectsItems: Set<string> = new Set();
  private forceNotifyValidation: boolean = false;
  private _resetStatus: boolean;
  private unsavedHandlerId: number;
  private forms$: Observable<any>;
  private dependedProperties: Map<string, any> = new Map<string, any>();
  private dependencyMap: Map<string, { dataSourcePath: string; target: string }[]> = new Map();
  private _disabled: boolean = false;
  private _disableForce: boolean = false;
  private internalReset: boolean = false;
  private _localForm: boolean;
  private previousNormalizedFormData: any;

  // Update on init for default value set
  @Input()
  public notifyDefaultValueInit: boolean = false;

  // form change emitting even if current form is invalid
  @Input()
  public emitFormChangeEvenIfInvalid: boolean = false;

  // Usage of this flag should be restricted as it emits of every event
  @Input()
  public forceEmit: boolean = false;

  // formsconfig has been reloaded in listen to value changes causing performance hit
  // This flag will avoid that
  @Input()
  public noConfigMutation: boolean = false;


  @Input()
  public isSaveOnChange: boolean = false;

  /**
   * add to the diff field map on value update
   */
  @Input()
  public flagDiffFieldsOnUpdate: boolean = false;

  /**
   * Callback after save works only if
   * Set saveFormCallback to capture callback after save
   */
  @Input()
  public isSaveDataOnsubmit: boolean = false;
  @Input()
  public hideFieldsOnRuntime: boolean = false; // Enables to hide fields on runtime. If this flag is true, only the field from 'showOnlyFields' will be shown (even if they were defined as visible in the configuration).
  @Input()
  public setLabelsOnRuntime: boolean = false; // Enables to change fields labels on runtime.
  @Input()
  public bubbleUpToWidget: boolean = false; //Enable to update form value to it's containing widget if needed, for reflection in the containing form for example
  @Output() saveFormCallback: EventEmitter<any> = new EventEmitter(); // Works only if isSaveDataOnsubmit is true
  @Input()
  public onSaveDataSuccessMessage?: string = this.notificationMessage.DATA_SAVED_SUCCESSFULLY;
  @Input()
  public onSaveDataErrorMessage?: string = this.notificationMessage.UNABLE_TO_SAVE_DATA;

  /******* GEN-16525 diff mitigation - start ******/

  /**
   * query and save context for updatedFields resource
   */
  @Input() updatedFieldsContextType: string = 'WorkflowRevision';
  private _updatedFieldsContext: string;
  private _cancelFetchUpdatedFields: boolean = false; //Optimization - prevent fetching chnages when not needed. In use when updatedFieldsContext is defined but form was just opened for the first time and there is no need to fetch changes (but need to save new changes).

  /**
   * Set of changed fields names
   * **/
  private _diffFields: Set<string>;

  @Input() unsavedHandler: 'none' | 'parent' | 'form' = 'parent';

  @Input() mode: 'form' | 'text';

  private _overrides = { };

  @Input() required: boolean = true;

  @Input() updateOnBlur: boolean = null;

  private _editMode: boolean = false;

  /**
   * In case we load the dynamic form inside of a popup we would like the dropdown and calender options
   * to overflow to outside of the popup in case of overflow, default is false
   * @type {boolean}
   */
  @Input() popupMode: boolean = false;

  /**
   * fields to disable in case the form isn't disabled
   * @type {Array<string>} properties name
   */
  @Input() disabledFields: string[] = [];

  /**
   * fields to enable in case the form is disabled
   * @type {Array<string>} properties name
   */
  @Input() enabledFields: string[] = [];

  @Input() title?: string;

  @Input() titleClass?: string;

  @Input() subTitle?: string;

  @Input() requiredMessage?: string = 'Required information missing';

  private _formConfig: Array<IdynamicFormItem>;

  @Input() additionalSearchValues?: any;

  @Input() dynamicForm?: FormGroup;

  private _formData: any;
  private isFormConfigAvailable: BehaviorSubject<boolean>;
  private saveHandler: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  @Input() submitButtonText?: string = null;

  @Input() cancelButtonText?: string = null;

  private _formVertical: boolean = false;

  // In case formData input change, update form controls values.
  @Input() listenToDataChange: boolean = false;
  @Input() visibleFieldsDetails: IWorklistColumnItem[];
  @Input() set dataChanged(changed: boolean) {
  }

  // In case formData input change, update form controls values.
  @Input()
  public loaderType: LOADER_TYPES =  DEFAULT_LOADER_FOR_DYNAMIC_FORM;

  // Set the form width to 50% , so you can another form behind
  @Input()
  formHalfWidth: boolean = false;

  @Input()
  formItemFullWidth: boolean = false;

  /**
   * Setting the stayInFormMode input to true will keep the input fields as input display even after blur
   */
  @Input()
  stayInFormMode: boolean = false;

  @Input() note: string = '';

  @Input() globalConfigurations: any = undefined;

  /**
   * If saveFormControlsInStore turned on, the form's controls will be saved and updated in the store along with the control's validation status.
   * It could be used by the form's components to determine the form's validation status, invalidation reason and invalid form controls.
  */
  @Input() saveFormControlsInStore: boolean = false;

  _submitted: boolean = false;

  public tunnel$: Subject<any>;
  public callback: (value) => Observable<Response>;
  private validateValue: boolean;
  private destroyer: Subject<any> = new Subject();
  private destroyed: boolean = false;
  public widgetUpdateValueCallback: (value) => void;
  public widgetLoadDone: () => void;

  private static checkIfFieldChanged(changes: SimpleChanges, fieldName: string) {
    return changes[fieldName] && (changes[fieldName].firstChange || (changes[fieldName].currentValue !== changes[fieldName].previousValue));
  }

  public formName() {
    return this.formIdentifier;
  }

  private triggerReflectsForField(fields: string[]) {
    fields.forEach(field => this.runItemReflects(field, get(this.dynamicForm.controls, field + '.value')));
  }


  ngOnInit() {
    this.handleUnsavedChangesAlert(true);
    super.init(this.formId);
    this.onChangeSaveHandler();

    if(this.formIdentifier?.includes('PATHOLOGY')) {
      this.unitHeaderClass = 'pathology';
    }
    else if(this.formIdentifier === 'RADIOLOGY') {
      this.unitHeaderClass = 'radiology';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (DynamicFormComponent.checkIfFieldChanged(changes, 'formName')
      || DynamicFormComponent.checkIfFieldChanged(changes, 'formId')
      || DynamicFormComponent.checkIfFieldChanged(changes, 'disabledFields')
      || (
        (this.listenToDataChange || (changes.formData && changes.formData.firstChange))
        && DynamicFormComponent.checkIfFieldChanged(changes, 'formData')
      )
    ) {
      this.load = false;

      if (this.dynamicForm) {
        this.reset();
      }

      // TODO this code only if required have from redux  put it on ngInit
      this.initForm();
    }
  }

  ngOnDestroy(): void {
    this.destroyer.next();
    this.destroyer.complete();
    this.destroyed = true;
    this.cleanUp();
    super.OnDestroy();
  }

  cleanUp() {
    this.handleUnsavedChangesAlert(false);

    if (this.formConfig) {
      this.formConfig.forEach((item) => {
        delete item.additional;
      });
    }

    this.cleanLocalForm();
  }

  private cleanLocalForm() {
    if (this._localForm) {
      this.formHandler.removeForm(this.formId);
      this.formIdentifier = null;
      this._localForm = false;
    }
  }

  public addControl(config: IdynamicFormItem) {
    const { value, validators} = this.buildControl(config);
    const itemDisabled = this.getItemDisableStatus(config);

    if (this.dynamicForm.controls[config.name]) { // Handles GEN-35674
      this.dynamicForm.controls[config.name].setValidators(this.getValidators(config));
      this.dynamicForm.controls[config.name].updateValueAndValidity();

      if (itemDisabled) {
        this.dynamicForm.controls[config.name].disable();
      }
      else {
        this.dynamicForm.controls[config.name].enable();
      }
    }
    else {
      this.dynamicForm.addControl(config.name, new FormControl({
        value: value,
        disabled: itemDisabled
      }, validators));
    }
  }

  private generateUUID() {
    this._localForm = true;
    this.formId = `${DynamicFormComponent.GENERATED_FORM_ID_PREFIX}${StringUtils.generateGuid()}`;
    this.initForm();
  }

  onBundleSubscribe(dataAsyncLoader: object, formKey: string = null, isFromConfigBinding: boolean = false,  isContinuous: boolean = false, updateFromDataBinding: boolean = false) {
    if (this.widgetFormInstance && updateFromDataBinding && this.bubbleUpToWidget) {
      this.updateDataBinding = updateFromDataBinding;
    }
    isFromConfigBinding ? this._externalDataReset = true : this._externalDataReset = false;

    if (isFromConfigBinding && !isEmpty(dataAsyncLoader)) {
      this.formData = Object.assign({ }, this.dynamicForm.value, dataAsyncLoader);
    } else {
      this.formData = Object.assign({ }, this.formData, dataAsyncLoader);
    }

    this.initForm();
    this.dataRecieved.emit(true);
  }

  onConfigSetToOptions(_options: any, itemNameKey: string) {
    const items = this.formConfig.filter((item: IdynamicFormItem) => {
      return item.name === itemNameKey;
    });
    this.runReflect({ affectedField: itemNameKey,
      valueToSet: null,
      options: _options
    }, items[0], new Map());
  }

  onConfigSetToConfig(config: object, formKey: string) {
    this.formConfig.forEach((itemConfig: IdynamicFormItem) => {
      if (formKey === itemConfig.name) {
        itemConfig  = Object.assign(itemConfig, config);
        this.dynamicForm.controls[itemConfig.name].reset();
      }
    });
  }

  private initForm() {
    this.load = false;
    this.destroyer.next();
    this._nestedObjectsItems.clear();
     this.dataob$.pipe(
       takeUntil(this.destroyer.asObservable()),
       filter(value => !value && value !== undefined),
       tap(() => this.dataRecieved.emit(true))
     ).subscribe();
    this.forms$
      .pipe(
        takeUntil(this.destroyer.asObservable()),
        map((data: Map<string, any>) => {
          if (this.formId && !data.has(this.formId)) {
            if (this.formConfig) {
              this.formHandler.addForm(this.formId, this.formConfig, this.formReflects, null, this.configBindings);
            } else if (!this._localForm) {
              this.formHandler.getForm(this.formId).take(1).publish().connect();
            }
          }

          return data;
        }),
        filter((data) => data.has(this.formId)),
        map((data) => data.get(this.formId)),
        distinctUntilChanged(),
        debounce(() => this.dataob$.asObservable()) // handle data binding latency to debounce form creatio
      ).subscribe((data) => {

        this.copyStoreConfigToForm(); // get all the form config first then assign formConfig
        this.formConfig = isEmpty(this.overrides) ? data : cloneDeep(data);
        this.markDifferences();

        //Remove fields that shouldn't be shown
        if (this.hideFieldsOnRuntime && this.visibleFieldsDetails) {
          const visibleFields: Array<string> = this.visibleFieldsDetails.map(field => field.key);
          if (visibleFields.length){
            this.formConfig = this.formConfig.filter(item => {
              if (visibleFields.includes(item.name)){ //If the item.name (or alias) exists in the list the item will be visible
                return true;
              }
              if (!item.alias){
                return false;
              }

              if (!Array.isArray(item.alias)){
                return false;
              }

              //If the item has aliases, checking if one of the aliases exists in the list.
              //This is in use in case there should be a name condition on a certain field and the field can get data
              //from a few fields (e.g. filter panel form which corrolates with a certain grid column that can get data from different fields)
              return visibleFields.includes(item.name) || item.alias.some(elem => {
                return visibleFields.includes(elem);
              })
            }
            )
          }
        }
        if (this.setLabelsOnRuntime && this.visibleFieldsDetails) {
          //Override fields labels that were defined in form config (for example, it is in use in filters, when the filter label should be
          //taken from the relevant column in the current worklist - same column can have different names in different worklists).
            this.formConfig.forEach(item => {
              if (item.label && !item.label.startsWith('##')){ //do not replace empty labels or labels that starts with '##' - this can be used when a column has multiple keys and their multiple fiters should have different labels
                let currField = this.visibleFieldsDetails.find(field => field.key === item.name);
                if (!currField && item.alias){
                  currField = this.visibleFieldsDetails.find(field => item.alias.includes(field.key))
                }
                if (currField){
                  item.label = currField.label;
                  //There are cases in which the same field has different labels in differtn worklists but the place holder has a predifned value.
                  //In this case, if the place holder is defined as '##' in the configuration, the place holder will be the same as the label.
                  if (item.placeholder === '##'){
                    item.placeholder = currField.label;
                  }
                }
              }
            });
        }
        if (this.showFieldCount) {
          let countItems = 0 ;
          const inputConfigs: Array<IdynamicFormItem> = [];
          this.formConfig.forEach(config => {
            if (!includes(config['cssClass'], 'hidden-form-item')) {
              countItems++;
            }

            if (countItems <= this.showFieldCount ) {
              inputConfigs.push(config);
            }
          });

          this.formConfig = inputConfigs;
        }

        this.formConfig.forEach(conf => {
          this.replaceActionExecutor(this.formData, conf);
          if (!(this._externalDataReset)) {
            this.registerValueChange(this.formId, conf.configIds, conf.name, this.formData, true);
          }
        });

        this.build();
          if (!this.destroyed) {
            this.detector.markForCheck();
          }

      // Apply form level reflects
      this.runFormReflects(true);

      // Apply initialize item reflects
      this.formConfig.forEach(conf => {
        this.runItemReflects(conf.name, get(this.dynamicForm.value, conf.name), true);
      });

      const normalizedForm = this.normalizeFormData(this.dynamicForm.getRawValue());
      this.previousNormalizedFormData = cloneDeep(normalizedForm);
        if ( !this.disabled && this.updatedFields.size > 0 ) {
          this.formChange.emit({ form: normalizedForm, updated: Array.from(this.updatedFields)});
          this.updatedFields.clear();
        }
        else {
          this.informationForCard(normalizedForm);
        }

      this.formInit.emit(normalizedForm);
      this.formInitCompleted.emit({ formData: normalizedForm, formId: this.formId});
  });
  }


  private build() {
    if (!this.formConfig) {
      return;
    }

    const controls = { }, formDataClone = { ...this.formData};
    this.dependencyMap.clear();

    for (const item of this.formConfig) {
      item.isRootProperty = FormHandlerService.IsRootProperty(item.name);
      this.setItemValueType(item);
      let { value, validators } = this.buildControl(item);
      const itemDisabled = this.getItemDisableStatus(item);
      delete formDataClone[item.name];

      if (get(item, 'attributes["[hideEmptyArrayItem]"]', false) && (Array.isArray(value))) {
        _remove(value, function(c) {
            return (c.display === "");
          });
      }

      if (!item.isRootProperty) {
        controls[item.name] = [{
          value: value,
          disabled: itemDisabled
        }, validators];
      }

      item.isItDefault = this.isItDefaultValue(item,value);
      this.buildDependencyMap(item);
    }

    // formDataClone contains values that were added by the component that uses the form.
    // The values are added to 'controls' for later usage (like links parameters).
    if (this.mapAllProperties) {
      Object.keys(formDataClone).forEach((key) => {
        controls[key] = [{
          value: formDataClone[key],
          disabled: false
        }, []];
      });
    }

    this.dynamicForm = this.form.group(controls);

    if (this.updateDataBinding) {
      const normalizedForm = this.normalizeFormData(
        Object.entries(this.dynamicForm.controls)
        .reduce(((acc, [controlId, controlData]) => {
          acc[controlId] = controlData.value;
          return acc;
        }), {})
      );
      this.widgetUpdate(normalizedForm);
      this.updateDataBinding = false;
    }

    this.setWidgetFormBasePayload();
    this.listenToValueChanges();
    this.getChangedFields();
    this.forceNotifyValidation = true;
    this.triggerValidate();
    this.load = true;
    this.dynamicForm.removeControl(''); // for emitting current form value - for the use of distinctUntilChanged later
  }

  private buildControl(item) {
    let value, validators;
    let isDefaultValue = true;
    if (toPath(item.name).length > 1) {
      this._nestedObjectsItems.add(item.name);
    }

    try {
      const formDataValue = get(this.formData, item.name, undefined);

      if (this.overrides[item.name]) {
        Object.assign(item, this.overrides[item.name]);
      }

      // GEN-173619 - fixed due to problem with default values settings
      if(!!this._formData && has(this._formData,'isFieldsRequired') && get(this._formData,'isFieldsRequired', true) === false){
        item.required = false;
      }

      validators = this.getValidators(item);
      value = cloneDeep(item.defaultValue);

      switch (ITEMS_TYPES[(<string> item.type).toUpperCase()]) {
        case ITEMS_TYPES.DATEPICKER: {
          const datePickerValue = this.extractDatePickerValue(formDataValue,value);
          value = datePickerValue.value;
          isDefaultValue = datePickerValue.isDefaultValue;
          break;
        }
        default:
          if (formDataValue || (formDataValue !== null && formDataValue !== undefined) && formDataValue.length > 0 || formDataValue === 0) {
            const isFormDataOfTypeArray = Array.isArray(formDataValue);
            if (
              (isFormDataOfTypeArray && formDataValue.length > 0)
              || !isFormDataOfTypeArray) {
                if (item.additional && item.additional.uniqueValues) {
                  value = this.getUniqueItems(formDataValue, item.additional.uniqueValues);
                } else {
                    value = formDataValue;
                  }
                }
              isDefaultValue = false;

            if (!isFormDataOfTypeArray && formDataValue instanceof Object){
              if (item.pathToField) {
                value = get(formDataValue, item.pathToField);
                isDefaultValue = false;
              }
              if (item.additional && !item.additional.allowEmptyObjectAsValue && isEmpty(formDataValue)) {
                value = item.defaultValue;
                isDefaultValue = true;
              }
            }
          } else if (!formDataValue && item.pathToField) {
            try {
              value = jmespath.search(this.formData, item.pathToField);
              isDefaultValue = false;
            } catch (err) { }
          }
          break;
      }
    } catch (e) {
      value = null;
    }

    if ((<string> item.type) && ITEMS_TYPES[(<string> item.type).toUpperCase()] === ITEMS_TYPES.TEXT) {
      value = StringUtils.replaceNewLineCharBySpace(value);
    }

    if (this.notifyDefaultValueInit && !this.editMode && isDefaultValue && !item.ignoreDiff) {
      this.updatedFields.add(item.name);
    }

    return { value, validators};
  }

  private isItDefaultValue(item:IdynamicFormItem,currentValue:any):boolean{

     if(isEmpty(currentValue)) {
       return false;
     }

    if(item.defaultValue && (isEqual(item.defaultValue,currentValue))){
       return true;
     }
     else if(Array.isArray(this._dataBindings) && this._dataBindings.length > 0) {
      const currentDatabinding: IdataBinding = this._dataBindings.find(binding => binding.identifier == item.name);
      if (currentDatabinding && currentDatabinding.hasOwnProperty('defaultValue')) {
        const dataBindingValue = item.hasOwnProperty('pathToField') ? get(currentDatabinding.defaultValue, item.pathToField) : currentDatabinding.defaultValue;
        return isEqual(dataBindingValue,currentValue);
      }
      else {
        return false;
      }
    } else {
      return false;
    }

}
  private extractDatePickerValue(formDataValue: any,defaultValue: any): { value: any, isDefaultValue: boolean } {
    let value: any = defaultValue;
    const tempValue = [];
    let isDefaultValue = true;

    if (Array.isArray(formDataValue)) {
      value = [];
      formDataValue.forEach(dates => {
        if (dates) {
          tempValue.push(moment(dates).toDate());
        }
      });
      tempValue.forEach(tempDate => {
        value.push(tempDate.toPlatformFormat());
      });
    }
    else if (formDataValue) {
      value = moment(formDataValue).toDate();
      isDefaultValue = false;
      value = value.toPlatformFormat();
    }
    else if (value && value.toLowerCase() === 'now' && formDataValue === undefined) {
      value = new Date();
      value = value.toPlatformFormat();
    }
    else {
      value = formDataValue;
    }

    return { value, isDefaultValue };
  }

  private getUniqueItems(data, uniqConfig) {
    return _uniqBy(data, (item) => {
      if (uniqConfig && Array.isArray(uniqConfig.paths)) {
        return uniqConfig.paths.map(path => {
            try {
              return jmespath.search(item, path);
            } catch (err) {
              // the search function will throw an error if it is not able to complete the search for any reason
              return '';
            }
          }).join('');
      }
      return data;
    });

  }

  private getItemDisableStatus(item: IdynamicFormItem) {
    let disabled = this.disabledForced;
    disabled = disabled || item.disabled;
    disabled = disabled || (this.disabled && item.forceEnable !== true && !this.enabledFields.includes(item.name));
    disabled = disabled || (this.disabledFields && this.disabledFields.includes(item.name));

    return disabled;
  }

  private setWidgetFormBasePayload() {
    this.formBaseWidgetPayload = DynamicFormModalPayload.builder()
      .setPopupMode(this.popupMode)
      .setDisabled(this.disabled)
      .setListenToDataChange(this.listenToDataChange)
      .setLoaderType(this.loaderType)
      .setEditMode(this.editMode)
      .setAdditionalSearchValues(this.additionalSearchValues)
      .setFormContext(this.updatedFieldsContext)
      .setFormContextType(this.updatedFieldsContextType)
      .setFlagDiffFieldsOnUpdate(this.flagDiffFieldsOnUpdate)
      .setGlobalConfigurations(this.globalConfigurations);
  }

  private listenToValueChanges() {
    this.dynamicForm.valueChanges
      .pipe(
        takeUntil(this.destroyer.asObservable()),
        skipWhile(() => {
          return this._resetStatus;
        })
      )
      .pipe(
        tap(() => {
          this.triggerValidate();
          this.routeChangeHandler.setHandlerRequirement(this.unsavedHandlerId, true);
        }),
        filter(() => !this.internalReset),
        filter(() => {
          if (this.forceChange || !this.lastTouchedItem) {
            return true;
          }

          const itemConfig = this.getConfigItem(this.lastTouchedItem)[0];
          if (!itemConfig) {
            return true;
          }

          if (this.isRootItem(itemConfig) || this.isFormControlItemHasForceUpdate(itemConfig)) {
            return true; // in case of widget, don't filter (filtering done by the widget level
          }
          if (this.emitFormChangeEvenIfInvalid) {
            return (this.lastTouchedItem && ((this.updateOnBlur && this.blured) || !this.updateOnBlur));
          }

          return (
            this.lastTouchedItem
            && ((this.updateOnBlur && this.blured) || !this.updateOnBlur)
            && (
              this.dynamicForm.valid
              || (
                this.dynamicForm.controls[this.lastTouchedItem] &&
                (
                  this.dynamicForm.controls[this.lastTouchedItem].valid
                  || get(itemConfig, 'attributes["[listenToInvalid]"]')
                )
              )
            )
          );
        }),
        tap(val => {
          if (this.lastTouchedItem && val[this.lastTouchedItem] instanceof WidgetValueWrapper) {
            // unwrap widget primitive value
            val[this.lastTouchedItem] = val[this.lastTouchedItem].valueOf();
            this.dynamicForm.controls[this.lastTouchedItem].patchValue(val[this.lastTouchedItem], { emitEvent: false, onlySelf: true});
          }
        }),
        filter((form) => {
          const itemConfig = this.getConfigItem(this.lastTouchedItem)[0];

          if (itemConfig && this.isRootItem(itemConfig)) {
            return !!this.lastTouchedItem;
          }
          if(get(itemConfig, 'attributes["[noEmptyEmit]"]') && (isEmpty(form[this.lastTouchedItem]))){
            return false;
          }

          const forceEmitInd = itemConfig?.forceEmit !== undefined ? itemConfig.forceEmit : get(itemConfig, 'attributes["[forceEmit]"]');
          return form[this.lastTouchedItem] != null || (!!itemConfig && (!itemConfig.required || forceEmitInd)) || this.forceChange;
        }),
        distinctUntilChanged((prev, curr) => {
          if (!this.lastTouchedItem) {
            return true;
          }

          if (this.forceChange) {
            return false;
          }

          if (get(curr, this.lastTouchedItem + '.__forceUpdate', null)) {
            const item = get(curr, this.lastTouchedItem , null);
            set(item, '__forceUpdate', false);
            this.dynamicForm.controls[this.lastTouchedItem].patchValue(item);
            return false;
          }
          return this.isTheSameValue(prev, curr);
        }),
        tap(() => {
          if (!this.forceEmit) {
            this.forceChange = false;
          }
        }),
        filter(() => !!this.lastTouchedItem),
        map((form) => {
          if (!this.isTheSameValue(this.previousNormalizedFormData, form) || this.forceChange ) {
              this.runFormReflects(false, this.lastTouchedItem);
              this.runItemReflects(this.lastTouchedItem, form[this.lastTouchedItem]);
              if(!this.noConfigMutation){ // looks like a small change but big performance gain in nested form. Will avoid unnecessary change detections
                this.formConfig = [...this.formConfig];
              }
              if(!this.isDynamicFormValid()){
                const normalizedForm = this.normalizeFormData(form);
                this.previousNormalizedFormData = cloneDeep(normalizedForm);
              }
          }
          return this.dynamicForm.getRawValue();
        }),
        tap(() => {
          if (this._formConfig) {
            const touchedItemConfig = this._formConfig.find(item => item.name == this.lastTouchedItem);
            if (touchedItemConfig && touchedItemConfig.ignoreDiff) {
              return;
            }
          }
          this.updatedFields.add(this.lastTouchedItem);
          const diffFields = this.diffFields;
          diffFields.push.apply(diffFields, Array.from(this.updatedFields));
          this.diffFields = diffFields;
        }),
        filter(() => this.isDynamicFormValid())
      )
      .subscribe((form) => {
        const normalizedForm = this.normalizeFormData(form);
        this.formChange.emit({ form: normalizedForm, updated: Array.from(this.updatedFields)});
        this.widgetUpdate(normalizedForm);
        this.updatedFields.clear();
        this.routeChangeHandler.setHandlerRequirement(this.unsavedHandlerId, false);
        let configOflastouched = this.getConfigItem(this.lastTouchedItem)[0];
        if (!(this._externalDataReset) && this.formId)  {
          const formIdName = this.formId.includes('DF_TEMP') ? normalizedForm.formId : this.formId;
          this.registerValueChange(formIdName, configOflastouched.configIds, this.lastTouchedItem, normalizedForm);
        }

        // this.runReflects(this.lastTouchedItem, get(this.dynamicForm.value, this.lastTouchedItem), false);
        if (this.isSaveOnChange ) {
          this.saveHandler.next(normalizedForm);
        }

        this._externalDataReset = false;
        // TODO
        // making last touched item should happen default except for the
        // widgets which needs events without data change like drag, click etc
        if(get(configOflastouched, 'attributes["[noEmptyEmit]"]')) {
          this.lastTouchedItem = null;
        }
        this.previousNormalizedFormData = cloneDeep(normalizedForm);
       });
  }

  private isDynamicFormValid(): boolean {
    return this.emitFormChangeEvenIfInvalid || this.dynamicForm.valid;
  }

  private isTheSameValue(prev, curr): boolean {
    const prevValue = this.getDiffValue(prev);
    const currValue = this.getDiffValue(curr);

    return (currValue.display === null && currValue.display === prevValue.display )
      || (prevValue.display === currValue.display && prevValue.code === currValue.code);
  }

  private widgetUpdate(form) {
    if (this.widgetUpdateValueCallback) {
      this.widgetUpdateValueCallback.apply(this, [form]);
    }
  }

  formalizeKeysToRetain(data: any, keysToRetainProperties: Array<ItoPathFromPath>): any {
    let returnValue = {};
    if(Object.keys(data).length === keysToRetainProperties.length) {
      returnValue = data;
    } else {
      keysToRetainProperties.forEach(property => {
        const retainedKeyWithValue = get(data, property.fromPath);
        set(returnValue, property.toKeyPath, retainedKeyWithValue);
      });
    }
    return returnValue;
  }

  private normalizeFormData(formData) {
    formData = { ...formData};

    const lastTouchedItemConfig = this._formConfig.find(item => item.name == this.lastTouchedItem);
    let keysToRetainProperties = get(lastTouchedItemConfig, ['keysToRetain']);
    if(!isEmpty(keysToRetainProperties) && Array.isArray(keysToRetainProperties) && !isEmpty(formData[this.lastTouchedItem])) {

      if(Array.isArray(formData[this.lastTouchedItem])) {
        let requiredFieldsData = [];
        formData[this.lastTouchedItem].map(data => {
          let val = this.formalizeKeysToRetain(data, keysToRetainProperties);
          requiredFieldsData.push(val);
        });
        set(formData, this.lastTouchedItem, requiredFieldsData);
      } else {
        let val = this.formalizeKeysToRetain(formData[this.lastTouchedItem], keysToRetainProperties);
        set(formData, this.lastTouchedItem, val);
      }
    }

    this._nestedObjectsItems.forEach(item => {
      const temp = formData[item];
      delete formData[item];
      set(formData, item, temp);
    });

    return formData;
  }

  private getDiffValue(item, takeByLastTouched: boolean = true): { code?: any, display?: any } {
    const config = this.getConfigItem(this.lastTouchedItem)[0];

    if (!(config)) {
      return undefined;
    }

    let valueProperties = config.valueProperties;

    if(!valueProperties && config.options){
      valueProperties = this._search.getTransformObject(config.options.resource);
    }
    const returnValue = { code: null, display: null};

    if (config.isRootProperty) {
      const str = JSON.stringify(item);

      return this.getFieldsToCompareFromValue({code: str, display: str}, config.fieldsToCompare); // root property should verify the whole object
    }

    item =  takeByLastTouched ? item[this.lastTouchedItem] : item;

    if (item === null) {
      return this.getFieldsToCompareFromValue(returnValue, config.fieldsToCompare);
    }

    if (Array.isArray(item)) {
      returnValue.display = item
        .map(value => this.getDiffValue(value, false))
        .map(value => value.display + '_' + value.code)
        .join();
    } else if (item && typeof item === 'object') {
      returnValue.code = item[get(valueProperties, 'code', 'code')];
      returnValue.display = item[get(valueProperties, 'display', 'display')];

      if (config.fieldsToPreserve) {
        if (Array.isArray(config.fieldsToPreserve)) {
          config.fieldsToPreserve.forEach(field => returnValue.code += item[field]);
        } else if (config.fieldsToPreserve === '*') {
          returnValue.code += JSON.stringify(item);
        }
      }

      if (config.fieldsToDisplay && Array.isArray(config.fieldsToDisplay)) {
        config.fieldsToDisplay.forEach(field => {
          if (typeof field === 'string') {
            returnValue.display += item[field];
          } else if (typeof field === 'object' && field.display) {
            returnValue.display += item[field.display];
          }
        });
      }

      if (config.keysToRetain && Array.isArray(config.keysToRetain) && !isEmpty(config.keysToRetain)) {
        config.keysToRetain.forEach(field => returnValue.code += item[field.fromPath]);
      }
    } else {
      returnValue.display = item;
    }

    return this.getFieldsToCompareFromValue(returnValue, config.fieldsToCompare);
  }

  /**
   * Extract from form value only fields that are set for comparison
   *
   * @param {code: any, display: any} value
   * @param {Array<string>} fieldsToCompare - fields to compare from form config
   * @returns { code?: any, display?: any }
   */
  private getFieldsToCompareFromValue(value: { code: any, display: any }, fieldsToCompare: Array<string>): { code?: any, display?: any } {
    if (!fieldsToCompare) {
      return value;
    }
    let returnValue: { code?: any, display?: any } = {};
    fieldsToCompare.forEach((field) => {
      returnValue[field] = value[field];
    })
    return returnValue;
  }

  /**
   * Returning the form config items by name or id,
   * if cache map pass, the item will be cache and retrieve from it
   *
   * @param {string} itemNameOrId
   * @param {Map<string, IdynamicFormItem>} cachedItemsMap
   * @param {Array<string>} clearAdditionalFields - fields to delete from the 'additional' property
   * @param {boolean>} deepCloneItem - Deep clone found item and store the new pointer in the same position of the config array
   * @returns {IdynamicFormItem[]}
   */
  private getConfigItem(
    itemNameOrId: string,
    cachedItemsMap: Map<string,
    IdynamicFormItem[]> = new Map(),
    clearAdditionalFields: Array<string> = [],
    deepCloneItem = false
  ): IdynamicFormItem[] {
    let foundItems: IdynamicFormItem[] = [];

    if (cachedItemsMap.has(itemNameOrId)) {
      foundItems = cachedItemsMap.get(itemNameOrId);
    } else {
      if (this.formConfig) {
        this.formConfig.forEach((item: IdynamicFormItem, index: number) => {
          if (!(item.name === itemNameOrId || item.id === itemNameOrId)) {
            return;
          }

          // deep clone found item for prevent mutable changes
          let returnItem;

          if (deepCloneItem) {
            this.deepCloneItem(index);
          }

          returnItem = this.formConfig[index];

          if (!returnItem.additional) {
            returnItem.additional = { };
          } else {
            clearAdditionalFields.forEach(field => {
              delete returnItem.additional[field];
            });
          }

          if (returnItem) {
            foundItems.push(returnItem);
          }
        });
      }
    }

    cachedItemsMap.set(itemNameOrId, foundItems);

    return foundItems;
  }

  private deepCloneItem(itemIndex: number) {
    if (itemIndex === -1) {
      return null;
    }

    if (!this.formConfig[itemIndex]['cloned']) {
      this.formConfig[itemIndex] = cloneDeep(this.formConfig[itemIndex]);
      this.formConfig[itemIndex]['cloned'] = true;
    }

    return this.formConfig[itemIndex];
  }

  private deepCloneItemByConfig(itemConfig: IdynamicFormItem) {
    const index = this.formConfig.findIndex(conf => conf === itemConfig);

    return this.deepCloneItem(index);
  }

  private runItemReflects(itemName: string, itemValue: any, init = false): void {
    const items = this.getConfigItem(itemName, new Map());
    // todo: check for optimization
    items.forEach(item => {
      if (item && item.reflects && Array.isArray(item.reflects)) {
        let changed = false;
        const itemsMap = new Map();
        item.reflects.forEach((reflect) => {
          if (!init || (init && reflect.checkOnInit)) {
            changed = true;
            const fields = this.runReflect(reflect, item, itemsMap);

            fields.forEach(field => {
                const changedItemConfig = this._formConfig.find(configItem => configItem.name == field);

                if (changedItemConfig && changedItemConfig.ignoreDiff) {
                  return;
                }

                this.updatedFields.add(field);
              });
          }
        });

        if (changed) {
          this.detector.markForCheck();
        }
      }
    });
  }

  private runFormReflects(init = false, listenToField ?: any): void {
    if ( this.formId.includes('DF_TEMP')) {
      this.executeFormReflects(this.formReflects, init, listenToField );
    }
    else {
      this.formHandler.getStoredReflectsByFormId(this.formId).pipe(
        take(1)
      ).subscribe((formReflects: IReflect[]) => {
        if (!isEmpty(formReflects)) {
          formReflects = cloneDeep(formReflects);
          this.executeFormReflects(formReflects, init, listenToField);
        }
      });
    }
  }

  /**
   * Returns true if listenToFields are empty and returns evaluated value based on the provided listenToField
   * presence in the reflect listenToFields
   * @param {IReflect[]} formReflects
   * @param {boolean} init
   * @param listenToField
   */
  public executeFormReflects(formReflects: IReflect[], init = false, listenToField ?: any) {
    if (listenToField && formReflects) {
      formReflects = formReflects.filter(formReflect => {
        if (!formReflect.listenToFields) {
          return true;
        }

        return formReflect.listenToFields.findIndex(field => field === listenToField) > -1;
      });
    }

    let changed = false;
    const itemsMap = new Map();

    if (formReflects && formReflects.length > 0) {
      formReflects.forEach((reflect) => {
        if (!init || (init && reflect.checkOnInit)) {
          changed = true;
          const fields = this.runReflect(reflect, null, itemsMap);
          if (reflect['ignoreUpdatedFields']) {
            return;
          }
          fields.forEach(field => {

              const changedItemConfig = this._formConfig.find(item => item.name == field);
              if (changedItemConfig && changedItemConfig.ignoreDiff) {
                return;
              }
              this.updatedFields.add(field);
            });
        }
      });
    }

    if (changed) {
      this.detector.markForCheck();
    }
  }

  /**
     * Run reflect on single item.
     * return array of fields which got value update while running reflect
     *
     * @param {IReflect} reflect
     * @param {DynamicFormItem} itemConfig
     * @param {Map<string, DynamicFormItem>}itemsMap
   */
  private runReflect(reflect: IReflect, itemConfig: IdynamicFormItem, itemsMap: Map<string, IdynamicFormItem[]>): string[] {
    let affectedValues: string[] = [];
    reflect.affectedField = Array.isArray(reflect.affectedField) ? reflect.affectedField : [reflect.affectedField];
    const affectedFields = reflect.affectedField;

    for (const field of affectedFields) {
      const reflectedItems = this.getConfigItem(field, itemsMap, DynamicFormComponent.REFLECT_FIELDS, true);

      // GEN-121729 - see comments in the reflect.iterface.ts
      const specItems = reflectedItems.filter(item => item.reflects && item.reflects.some(ref => !!ref.selfReflect));

      reflectedItems.forEach(reflectedItem => {
        if ((specItems.length === 0) || ((specItems.length > 0) && (_findIndex(specItems, reflectedItem) > -1))) {
          const updatedField = this.dynamicForm ? this.applyReflectOnItem(reflect, reflectedItem, itemConfig) : undefined;

          if (updatedField) {
            affectedValues = Array.isArray(updatedField) ? affectedValues.concat(updatedField) : [...affectedValues, updatedField];
          }
        }
      });
    }

    return affectedValues;
  }

  private applyReflectOnItem(reflect: IReflect, reflectedItem: IdynamicFormItem, itemConfig: IdynamicFormItem) {
    let field;
    const itemValue = getFormItemValue(this.dynamicForm.getRawValue(), itemConfig);
    const validateValue =  reflect.pathToCheck ?
      get(itemValue, reflect.pathToCheck, null) :
      getDisplayedValue(itemValue, itemConfig);

    // Interpolating Form Reflects
    if (get(reflect, 'interpolate')) {
      reflect = fillInterpolateString(reflect, this.dynamicForm.getRawValue());
    }

    // check applyRegex
    let valid = validateRegex(reflect, validateValue);

    // check applyNumericCheck min
    valid = valid && checkMin(reflect, validateValue);

    // check applyNumericCheck max
    valid = valid && checkMax(reflect, validateValue);

    valid = valid && checkConditions(reflect, validateValue);

    if (!valid) {
      return;
    }

    // set style classes
    setCssClass(reflect, itemValue, reflectedItem);

    // set form item label
    setLabel(reflect, itemValue, reflectedItem);

    // set form item placeholder
    setPlaceholder(reflect, itemValue, reflectedItem);

    // set form item placeholder
    setRemovePropertyFlag(reflect, itemValue, reflectedItem);

    setOptions(reflect, itemValue, reflectedItem);

    setDisplayPattern(reflect, itemValue, reflectedItem);

    setConfiguration(reflect, itemValue, reflectedItem);

    setMinAndMaxAttributes(reflect, itemValue, reflectedItem);

    // return if is root property - no real control for it
    if (reflectedItem && reflectedItem.isRootProperty) {
      return;
    }

    if (reflect.applyValuesTransformation) {
      const { value, affectedField} = applyValueTransformation(reflect, itemValue, reflectedItem);
      this.dynamicForm.controls[reflectedItem.name].patchValue(value,{ emitEvent: false, onlySelf: true});
      field = affectedField;
    }

    // set value
    const valueToSet = getValueToSet(reflect, itemValue);
    if (valueToSet) {
      const { value, affectedField} = valueToSet;
      this.dynamicForm.controls[reflectedItem.name].setValue(value);
      field = affectedField;
    }

    const valueToSetFromReflectedItem = getValueToSetFromReflectedItem(reflect, reflectedItem);
    if (valueToSetFromReflectedItem) {
      const { value, affectedField} = valueToSetFromReflectedItem;
      this.dynamicForm.controls[reflectedItem.name].setValue(value);
      field = affectedField;
    }

    const valueToSetFromBulk =  getValueToSetFromBulkValue(reflect, reflectedItem);
    if (this.dynamicForm.controls[reflectedItem.name] && valueToSetFromBulk) {
      const { value, affectedField} = valueToSetFromBulk;
      this.dynamicForm.controls[reflectedItem.name].setValue(value);
      field = affectedField;
    }

    // get required value
    const requiredToSet = getRequiredToSet(reflect, itemValue);

    const regexToSet = reflect.regexToSet !== undefined && reflect.regexToSet != null
      ? reflect.regexToSet
      : reflectedItem.regex;

    // set regex and required
    const validatorObject = Object.assign({ }, DynamicFormItem.from({ }), {
      regex: regexToSet,
      required: requiredToSet != null ? requiredToSet : reflectedItem.required,
      attributes: reflectedItem.attributes
    });
    this.dynamicForm.controls[reflectedItem.name].setValidators(this.getValidators(validatorObject as IdynamicFormItem));
    this.dynamicForm.controls[reflectedItem.name].updateValueAndValidity();
    //'reflectedItem.required is being set twice. Once inside runOutsideAngular and once outside. If it is set only inside runOutsideAngular then if required should be changed after user action, the asterisk is not refreshed.
    //If it is set only outside runOutsideAngular then a few auto tests will fail.
    this._ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        reflectedItem.required = validatorObject.required;
      });
    });
    reflectedItem.required = validatorObject.required;


    // set disabled status
    if (reflect.hasOwnProperty('disableStatusToSet')) {
      reflect.disableStatusToSet
        ? this.dynamicForm.controls[reflectedItem.name].disable()
        : this.dynamicForm.controls[reflectedItem.name].enable();
    }

    setUpdateFormInstanceOnReflect(reflect, reflectedItem);

    return field;
  }

  private markDifferences() {
    if (!this.updatedFieldsContext) {
      return;
    }

    this.diffFields.forEach(itemName => {
      const configItems = this.getConfigItem(itemName, undefined, undefined, true);

      if (configItems.length === 0) {
        return;
      }

      configItems.forEach(configItem => {
        configItem.additional = Object.assign({ }, configItem.additional, { diff: true});
      });



    });

    if (!isEmpty(this._formConfig)) {
      this._formConfig = [...this._formConfig];
    }

  }

  private updateChangedFields() {
    return this.dynamicForm && !!this.updatedFieldsContext && !!this.diffFields && this.dynamicForm.valid
      ? this.formHandler.setChangedFields(this.getChangedFieldsContext(), this.diffFields)
      : Observable.empty();
  }

  private getValidators(item: IdynamicFormItem) {
    const validators = [];

    if (item.regex) {
      const flag = item.regexFlag || '';
      const regex = new RegExp(item.regex, flag);
      validators.push(Validators.pattern(regex));
    }

    if (item.required && this.required) {
      validators.push(Validators.required);
    }

    if (item.attributes) {
      if (item.attributes.min || parseInt(<string> item.attributes.min) === 0) {
        validators.push(Validators.min(<number> item.attributes.min));
      }

      if (item.attributes.max || parseInt(<string>  item.attributes.max) === 0) {
        validators.push(Validators.max(<number> item.attributes.max));
      }
    }

    return validators;
  }

  public triggerValidate() {
    this.valid = this.dynamicForm.valid;
    if (this.saveFormControlsInStore) {
      this.formHandler.updateFormControls(this.formName(), this.dynamicForm);
    }
  }

  /**
   * Deprecated, please use reflects instead
   * @deprecated
   * @param config
   */
  buildDependencyMap(config: IdynamicFormItem) {
    if (config.dependedProperties && config.dependedProperties.length > 0) {
      this.dependedProperties.set(config.name, config.dependedProperties);
    }

    if (config.dependOn && config.dependOn.length > 0) {
      config.dependOn.forEach(dependency => {
        if (!this.dependencyMap.has(dependency.sourcePath)) {
          this.dependencyMap.set(dependency.sourcePath, []);
        }

        this.dependencyMap.get(dependency.sourcePath).push({
          dataSourcePath: dependency.sourceProperty,
          target: config.name
        });
      });
    }
  }

  updateLastTouch(payload) {
    this.internalReset = false;
    this.lastTouchedItem = payload.name;
    this.blured = payload.blured;
    this.itemTouched.emit(payload.name);
  }



  updateSubmitStatus($event) {
    this.submitted=$event;
  }

  trackByFn(index, item) {
    return item.name
      ? `${this.formId}_${item.name}_${index}_${item.editable}_${item['lastReflect']}`
      : `${this.formId}_${index}_${item.editable}_${item['lastReflect']}`;
  }

  cancelClick() {
    this.cancelEmitter.emit();
  }

  onSubmit({ value, valid}: { value: any, valid: boolean }) {
    this.submitted = true;
    if (valid) {
      this.submitEmitter.emit(value);
      this.widgetUpdate(value);

      if (this.callback) {
        this.callback(value);
      }

      if (this.isSaveDataOnsubmit) {
        this.load = true;
        this._aggregationService.saveFormData(this.formIdentifier, value, [], 'flatten').subscribe(saveResponse => {
          if (get(saveResponse, 'code') === 2 && this.load) {
            this.notificationMessageService.success(this.onSaveDataSuccessMessage);
            this.saveFormCallback.emit({ response: saveResponse});
            this.load = false;
          } else if (get(saveResponse, 'code') === 4 && this.load) {
            this.notificationMessageService.error(this.onSaveDataErrorMessage);
            this.load = true;
          }
        });
      }

    }
  }

  private reset() {
    this.internalReset = true;
    this.validateValue = null;

    if (this.dynamicForm) {
      this.dynamicForm.reset();
    }
  }

  private getChangedFields() {
    if (!this.updatedFieldsContext || this._cancelFetchUpdatedFields) {
      return;
    }

    const queryContext = this.getChangedFieldsContext().map(context => context.reference);
    this.formHandler.getChangedFields(`[${queryContext.join(',')}]`)
      .subscribe((fields) => {
        if (this._diffFields) {
          fields.forEach(field => this._diffFields.add(field)); // Keeping _diffFields in case changes were detected by 'flagDiffFieldsOnUpdate'
        }
        else {
          this._diffFields = new Set<string>(fields);
        }
        this.markDifferences();
      });
  }

  private getChangedFieldsContext(): Array<ResourceReference> {
    const references = this._localForm ? [] : [new ResourceReference(this.formName(), 'Form')];
    const contexts = this.updatedFieldsContext.split(',').filter(ctx => !!ctx);
    const types = this.updatedFieldsContextType.split(',').filter(type => !!type);
    let j = 0;

    for (let i = 0; i < contexts.length; i++) {
      references.push(new ResourceReference(contexts[i], types[j]));

      if (j + 1 < types.length) {
        j++;
      }
    }

    return references;
  }

  private handleUnsavedChangesAlert(register: boolean) {
    switch (this.unsavedHandler.toLowerCase()) {
      case 'form':
        if (register) {
          this.unsavedHandlerId = this.routeChangeHandler.register();
        }

        break;
      case 'parent':
        if (!register && this.updatedFields.size > 0) {
          return this.unsavedEmitter.emit(this.updatedFields);
        } else if (register) {
          this.unsavedHandlerId = this.routeChangeHandler.register(false);
        }
      /* falls through */
      case 'none':
      default:
        return;
    }

    if (!register) {
      this.routeChangeHandler.unregister(this.unsavedHandlerId);
    }
  }

  linkClicked($event) {
    this.linkClick.emit($event);
  }

  public isRootItem(itemConfig: IdynamicFormItem) {
    return itemConfig.isRootProperty;
  }

  private isFormControlItemHasForceUpdate(itemConfig: IdynamicFormItem) {
    let valueToCheck;

    if (this.isRootItem(itemConfig)) {
      valueToCheck = this.dynamicForm.getRawValue();
    } else if (this.dynamicForm.controls[itemConfig.name]) {
      valueToCheck = this.dynamicForm.controls[itemConfig.name].value;
    } else {
      return false;
    }

    return get(valueToCheck, '__forceUpdate', false);
  }

  private setItemValueType(item: IdynamicFormItem) {
    item.valueType = item.valueType ? item.valueType : VALUE_TYPES.AUTO;
    if(<string> item.valueType){
      switch ((<string> item.valueType).toUpperCase()) {
        case VALUE_TYPES.AUTO:
          item.valueType = getValueTypeByItem(item);
          break;
        default:
          break;
      }
    }
  }

  private copyStoreConfigToForm() {
    this.formHandler.getStoredRootConfigByFormId(this.formId).pipe(
      take(1)).subscribe((config) => {
      this.emitFormChangeEvenIfInvalid = get(config, 'emitFormChangeEvenIfInvalid', this.emitFormChangeEvenIfInvalid);
      this.loaderType =  get(config, 'loaderType', this.loaderType);
      this.isSaveOnChange = get(config, 'isSaveOnChange', this.isSaveOnChange);
      this.isSaveDataOnsubmit = get(config, 'isSaveDataOnsubmit', this.isSaveDataOnsubmit);
      this.isSaveDataOnsubmit = get(config, 'isSaveDataOnsubmit', this.isSaveDataOnsubmit);
      this.hideFieldsOnRuntime = get(config, 'hideFieldsOnRuntime', this.hideFieldsOnRuntime);
      this.setLabelsOnRuntime = get(config, 'setLabelsOnRuntime', this.setLabelsOnRuntime);
      this.bubbleUpToWidget = get(config, 'bubbleUpToWidget', this.bubbleUpToWidget);
      this.forceEmit = get(config, 'forceEmit', this.forceEmit);
      this.noConfigMutation = get(config, 'noConfigMutation', this.noConfigMutation);
      this.onSaveDataSuccessMessage = get(config, 'onSaveDataSuccessMessage', this.onSaveDataSuccessMessage);
      this.onSaveDataErrorMessage = get(config, 'onSaveDataErrorMessage', this.onSaveDataErrorMessage);
    });
  }

  /**
   * This triggers the on change data to save when the save handler emits data
   * when onchange save is enabled for the widget. Distinct is handled with the lastTouchedItem.
   */
  private onChangeSaveHandler(){
    this.saveHandler.asObservable()
      .pipe(filter(normalizedForm => !!normalizedForm),
        distinctUntilChanged(( prev,curr ) => {
          let a = prev[this.lastTouchedItem];
          let b = curr[this.lastTouchedItem];
          return isEqual(a,b);
        }),
        tap((normalizedForm)=>{
          this.onSubmit({ value: normalizedForm, valid: this.dynamicForm.valid})
        })).subscribe();
  }

  //This method checks for attributes emitToCard and actionForCard in form configuration.
  // If present then it puts the form item value and action in to an array. Same array is emitted to panCancerCard component.
  private informationForCard(formdata: any){
    let emitToCardItems:{value:any, action:string}[] = [];
    this.formConfig.forEach(item => {
      if(has(item, 'attributes.actionForCard') && get(item,'attributes.emitToCard') && get(formdata, item.name)) {
        emitToCardItems.push({value: get(formdata, item.name), action: get(item, 'attributes.actionForCard')});
      }
    });
    if(!isEmpty(emitToCardItems)) {
      this.cardInfoEmitter.emit({information: emitToCardItems, action: CARD_ACTIONS.EVENT_PANEL});
    }

  }

  public hasDataBindings(): boolean {
    return !!this._dataBindings && this._dataBindings.length >0;
  }

  public getDataBindings(): Array<IdataBinding> {
    return this._dataBindings;
  }

  updateValidate(valid: boolean) {
    this.isValid = valid;
    this.normalSpecimenStatus = valid;
  }
}
